import React, { useState, useEffect, useMemo } from "react";

import {
  Popover, Button,
} from "antd";
import Highlighter from "react-highlight-words";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import SuccessAnimation from "../../../Common/UIComponents/SuccessAnimation";
import AddressDetails from "../../Common/AddressDetails";
import {
  DEFAULT_DELIVERY_DETAIL,
  isAddressDetailValid,
  parseSuppliesAddressFromResponse,
  parseSuppliesAddressList,
  parseCustomerAddressFromData,
} from "./DeliveryDetailDrawer";
import InventoryCardStepper from "./InventoryCardStepper";
import RecurrenceDateDrawer from "./RecurrenceDateDrawer";
import SpecialInstructionDrawer from "./SpecialInstructionDrawer";

import { getUserCredentials } from "../../../../utils/common";
import { getRecurringEventReadableText } from "../../../Env/utils";

import {
  createSupplyRequest,
  editSupplyRequest,
  createEditSupplyRecurrenceRequest,
  editSupplyRecurrenceRequest,
} from "../../../../actions/supplyPickup";

// import { ReactComponent as ExclamationIcon } from "../../../../assets/images/sample-submission/exclamationIcon.svg";
import { ReactComponent as DeleteBinIcon } from "../../../../assets/icons/Sample Submission Redesign/delete-bin-line.svg";
import { ReactComponent as DefaultCalendarIcon } from "../../../../assets/images/sample-submission/SampleCalendarIcon.svg";
import { ReactComponent as FileCopyIcon } from "../../../../assets/images/supply-pickup/FileCopy.svg";

import "./ItemsRequestedPanel.css";

const ItemsRequestedPanel = ({
  requestedItemsMap,
  inventoryItems = [],
  inventoryLoading = false,
  updateQuantity,
  returnToMainPage,
  onCreateSuccess,
  editingData,
  loadingEditingData,
  isRecurring = false,
  customerInfo,
}) => {
  const [openRecurrenceDrawer, setOpenRecurrenceDrawer] = useState(false);
  const [openInstructionDrawer, setOpenInstructionDrawer] = useState(false);
  const [addressLoading, setAddressLoading] = useState(true);
  const [deliveryDetail, setDeliveryDetail] = useState(DEFAULT_DELIVERY_DETAIL);
  const [savedSuppliesAddresses, setSavedSuppliesAddresses] = useState([]);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [spInstruction, setSpInstruction] = useState("");
  const [recurrence, setRecurrence] = useState(isRecurring);
  const [openPopover, setOpenPopover] = useState(false);
  const [recurrenceData, setRecurrenceData] = useState({
    dates: [],
    frequency: "",
    start_date: "",
    end_date: "",
    wordsToMark: [],
    text: "",
  });

  const handleSaveRecurrenceData = (data) => {
    const {
      dates, frequency, start_date, end_date,
    } = data;
    const { wordsToMark, text } = getRecurringEventReadableText(dates, frequency);
    setRecurrenceData({
      dates,
      frequency,
      start_date,
      end_date,
      wordsToMark,
      text,
    });
    setOpenRecurrenceDrawer(false);
  };

  const applyCustomerAddress = () => {
    if (editingData) {
      const {
        delivery_details, dates, frequency, start_date, end_date,
      } = editingData;
      setDeliveryDetail(parseCustomerAddressFromData(delivery_details));
      setSpInstruction(editingData.comments);
      if (dates && dates.length > 0) {
        handleSaveRecurrenceData({
          dates, frequency, start_date, end_date,
        });
        setRecurrence(true);
      }
    }

    if (customerInfo?.success) {
      const { address } = customerInfo;
      const suppliesAddressList = parseSuppliesAddressList(address?.supplies_address);
      setSavedSuppliesAddresses(suppliesAddressList);
      if (!editingData) {
        setDeliveryDetail(parseSuppliesAddressFromResponse(address));
      }
    } else if (!editingData) {
      toast.error(customerInfo?.message);
    }
    setAddressLoading(false);
  };

  useEffect(() => {
    if (!loadingEditingData && customerInfo) {
      applyCustomerAddress();
    }
  }, [editingData, loadingEditingData, customerInfo]); // eslint-disable-line

  useEffect(() => {
    let timer;
    if (showSuccessAnimation) {
      timer = setTimeout(() => {
        setShowSuccessAnimation(false);
        if (onCreateSuccess) {
          onCreateSuccess();
        } else {
          returnToMainPage("1");
        }
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessAnimation]); // eslint-disable-line

  const deliveryDetailsContainer = () => (
    <AddressDetails
      className="ItemRequested__AddressDetails"
      title="Delivery Address Details"
      selectId="supply-default-address"
      addresses={savedSuppliesAddresses}
      selectedAddress={deliveryDetail}
      loading={addressLoading}
      onSave={setDeliveryDetail}
    />
  );

  const validateDeliveryDetail = () => {
    const {
      name,
      email,
      phone,
      address_1,
      address_2,
      city,
      state,
      zipCode,
      country,
    } = deliveryDetail;
    const valid = isAddressDetailValid(deliveryDetail);
    const address = `${address_1}${address_2 ? `, ${address_2}` : ""}, ${city}, ${state} ${zipCode}, ${country}`;
    const delivery_details = {
      name,
      email,
      phone,
      address,
    };
    return { valid, delivery_details };
  };

  const getCreateRequestedBy = () => {
    const userInfo = getUserCredentials();
    return userInfo.uid === "support" ? "" : userInfo.user_name;
  };

  const submitRecurrenceRequest = async (delivery_details) => {
    setSubmittingRequest(true);
    const isEditRequest = Boolean(editingData);
    const {
      dates, frequency, start_date, end_date,
    } = recurrenceData;
    const payload = {
      items: Array.from(requestedItemsMap.values()).map((item) => {
        const { inventory_item_id, quantity, status: itemStatus } = item;
        let status = "requested";
        if (isEditRequest && itemStatus) {
          status = itemStatus;
        }
        return { inventory_item_id, quantity, status };
      }),
      delivery_details,
      comments: spInstruction,
      dates,
      frequency,
      start_date,
      end_date,
      requested_by: isEditRequest ? editingData.requested_by : getCreateRequestedBy(),
    };
    if (isEditRequest) {
      payload.supply_request_id = editingData.supply_request_id;
    }
    const { success, message } = isEditRequest
      ? await editSupplyRecurrenceRequest(payload)
      : await createEditSupplyRecurrenceRequest(payload);

    if (success) {
      setShowSuccessAnimation(true);
    } else {
      toast.error(message);
    }
    setSubmittingRequest(false);
  };

  const submitRegularRequest = async (delivery_details) => {
    setSubmittingRequest(true);
    const isEditRequest = Boolean(editingData);
    const payload = {
      items: Array.from(requestedItemsMap.values()).map((item) => {
        const { inventory_item_id, quantity, status: itemStatus } = item;
        let status = "requested";
        if (isEditRequest && itemStatus) {
          status = itemStatus;
        }
        return { inventory_item_id, quantity, status };
      }),
      delivery_details,
      comments: spInstruction,
      requested_by: isEditRequest ? editingData.requested_by : getCreateRequestedBy(),
    };
    if (isEditRequest) {
      payload.supply_order_id = editingData.supply_order_id;
      payload.request_type = editingData.request_type?.toLowerCase() === "recurring" ? "recurring" : "normal";
      payload.status = "requested";
    }
    const { success, message } = isEditRequest ? await editSupplyRequest(payload) : await createSupplyRequest(payload);

    if (success) {
      setShowSuccessAnimation(true);
    } else {
      toast.error(message);
    }
    setSubmittingRequest(false);
  };

  const handleSubmitRequest = async () => {
    setOpenPopover(false);
    const { valid, delivery_details } = validateDeliveryDetail();
    if (!valid) {
      toast.error("Delivery address is not valid, please contact Genista Biosciences");
      return;
    }
    const isRecurringTemplateEdit = Boolean(editingData && isRecurring);
    if (isRecurringTemplateEdit || (!editingData && recurrence)) {
      submitRecurrenceRequest(delivery_details);
    } else {
      submitRegularRequest(delivery_details);
    }
  };

  const submitClick = () => {
    handleSubmitRequest();
  };

  const popoverContent = () => (
    <div className="ItemRequestedPopoverBtnDiv">
      <Button type="default" onClick={() => handleSubmitRequest()}>
        No
      </Button>
      <Button type="primary" onClick={() => handleSubmitRequest(true)}>
        Yes
      </Button>
    </div>
  );

  // const handleRecurrenceCheckboxChange = (e) => {
  //   setRecurrence(e.target.checked);
  // };

  const renderSubmitBtnText = () => {
    let text = "Request Supply";
    if (editingData) {
      text = isRecurring
        ? "Update Recurring Request"
        : "Update Request";
    } else if (recurrence) {
      text = "Create Recurring Request";
    }
    return text;
  };

  const requestedItemsTitle = requestedItemsMap.size === 1 ? "Item Requested" : "Items Requested";

  const submitDisabledReason = useMemo(() => {
    const reasons = [];
    if (requestedItemsMap.size === 0) {
      reasons.push("Add at least one item to submit");
    }
    if (recurrence && recurrenceData.dates.length === 0) {
      reasons.push("Select recurring dates to submit");
    }
    return reasons.join(". ");
  }, [requestedItemsMap.size, recurrence, recurrenceData.dates.length]);

  const isSubmitDisabled = Boolean(submitDisabledReason);

  const inventoryItemIds = useMemo(
    () => new Set(inventoryItems.map((item) => item.inventory_item_id)),
    [inventoryItems],
  );

  const isItemUnavailable = (inventoryItemId) => (
    !inventoryLoading
    && !loadingEditingData
    && !inventoryItemIds.has(inventoryItemId)
  );

  return ((
    <>
      <div className="ItemsRequestedPanel">
        <div className="ItemsRequestedPanel__Header">
          <div className="ItemsRequestedPanel__HeaderLabel">
            <div className="ItemsRequestedPanel__HeaderAccent" aria-hidden />
            <h5>
              {requestedItemsTitle}
              {" ("}
              {requestedItemsMap.size}
              )
            </h5>
          </div>
          <Button
            type="default"
            className="add-instruction-button"
            icon={<FileCopyIcon className="ItemRequested__FileCopyIcon" />}
            onClick={() => setOpenInstructionDrawer(true)}
            disabled={loadingEditingData}
          >
            {spInstruction ? "Edit Instruction" : "Add Instruction"}
          </Button>
        </div>

        <div className="ItemsRequestedPanel__Body">
          <StyledScrollbar className={`SupplyInventoryItemsScrollbar RequestedPanel${spInstruction ? " SpInstruction" : ""}`}>
            <div className="ItemRequestedCardList">
              {Array.from(requestedItemsMap.values()).map((item) => {
                const {
                  inventory_item_id, quantity, image_url, item_name,
                } = item;
                const unavailable = isItemUnavailable(inventory_item_id);
                return (
                  <div
                    key={inventory_item_id}
                    className={`ItemRequestedCard${unavailable ? " ItemRequestedCard--unavailable" : ""}`}
                  >
                    <div className="ItemRequestedCard__ImageAndLabel">
                      <button
                        type="button"
                        className="ItemRequestedCard__DeleteBtn"
                        onClick={() => updateQuantity(inventory_item_id, 0)}
                        aria-label={`Remove ${item_name}`}
                      >
                        <DeleteBinIcon />
                      </button>
                      <div className="ItemRequestedCard__ImageContainer">
                        {image_url && <img src={image_url} alt={item_name} loading="lazy" />}
                        {!image_url && <span>No Image</span>}
                      </div>
                      <div className="ItemRequestedCard__ItemDetails">
                        {item_name && (
                          <span className="ItemRequestedCard__ItemName">{item_name}</span>
                        )}
                        {unavailable && (
                          <span className="ItemRequestedCard__UnavailableLabel">Item Not Available</span>
                        )}
                      </div>
                    </div>
                    <InventoryCardStepper
                      className="ItemRequestedCard__Stepper"
                      min={0}
                      value={quantity}
                      onChange={(qty) => updateQuantity(inventory_item_id, qty)}
                    />
                  </div>
                );
              })}
              {requestedItemsMap.size === 0 && (
                <div className="NoItems">
                  Add items to
                  <br />
                  Create Supply Request
                </div>
              )}
            </div>

            <div className="ItemsRequestedPanel__DetailsSection">
              {spInstruction && (
                <div className="ItemRequested__DeliveryDetails">
                  <div className="ItemRequested__DeliveryDetailsHeaderPanel">
                    <h6>Special Instructions</h6>
                    <Button type="text" color="primary" onClick={() => setOpenInstructionDrawer(true)}>
                      Edit
                    </Button>
                  </div>
                  <StyledScrollbar
                    autoHide
                    translateContentSizeYToHolder
                    className="SupplyInventoryItemsScrollbar special-instruction-text"
                  >
                    <div className="ItemRequested__SpecialInstructionsContent">
                      <span>{spInstruction}</span>
                    </div>
                  </StyledScrollbar>
                </div>
              )}
              {deliveryDetailsContainer()}
            </div>
          </StyledScrollbar>
          {/* <div className="ItemRequested__RecurrenceContainer">
            {/* <div className="checkboxDiv">
              <Checkbox checked={recurrence} disabled={loadingEditingData || addressLoading} onChange={handleRecurrenceCheckboxChange}>Make Recurrence Request</Checkbox>
              <StyledTooltip
                title="Recurring requests are scheduled automatically on the selected dates."
                placement="top"
              >
                <ExclamationIcon className="exclamationIcon" />
              </StyledTooltip>
            </div>
            {recurrence && recurrenceData.dates.length > 0 && (
              <StyledTooltip title={recurrenceData.text}>
                <p className="Frequency truncated-text">
                  <Highlighter
                    highlightClassName="Frequency--highlighted"
                    searchWords={recurrenceData.wordsToMark}
                    autoEscape
                    textToHighlight={recurrenceData.text}
                  />
                </p>
              </StyledTooltip>
            )}
          </div> */}
        </div>
      </div>

      <div className="CreateEditSupplyPanel__Footer">
        <div className="ButtonContainer">
          {recurrence && (recurrenceData.dates.length > 0 ? (
            <StyledTooltip title={recurrenceData.text}>
              <Button
                type="default"
                className="recurrence"
                icon={<DefaultCalendarIcon />}
                onClick={() => setOpenRecurrenceDrawer(true)}
              >
                <span className="Frequency truncated-text">
                  <Highlighter
                    highlightClassName="Frequency--highlighted"
                    searchWords={recurrenceData.wordsToMark}
                    autoEscape
                    textToHighlight={recurrenceData.text}
                  />
                </span>
              </Button>
            </StyledTooltip>
          ) : (
            <Button
              type="default"
              className="recurrence"
              icon={<DefaultCalendarIcon />}
              onClick={() => setOpenRecurrenceDrawer(true)}
            >
              {editingData && editingData.dates.length > 0 ? "Edit recurring date" : "Select recurring date"}
            </Button>
          ))}
          <StyledTooltip title={submitDisabledReason} placement="top">
            <span className="CreateEditSupplyPanel__SubmitWrap">
              <Popover
                open={openPopover}
                title="Apply changes on all recurring requests?"
                content={popoverContent}
              >
                <Button
                  type="primary"
                  className="submit"
                  onClick={submitClick}
                  disabled={isSubmitDisabled}
                  loading={submittingRequest}
                >
                  {renderSubmitBtnText()}
                </Button>
              </Popover>
            </span>
          </StyledTooltip>
        </div>
      </div>

      <RecurrenceDateDrawer
        open={openRecurrenceDrawer}
        onClose={() => setOpenRecurrenceDrawer(false)}
        onSave={handleSaveRecurrenceData}
        editingData={editingData}
      />
      <SpecialInstructionDrawer
        open={openInstructionDrawer}
        onClose={() => setOpenInstructionDrawer(false)}
        onSave={setSpInstruction}
        initialValue={spInstruction}
      />
      {showSuccessAnimation && (
        <SuccessAnimation message={editingData ? "Order Updated!" : "Order placed!"} />
      )}
    </>
  ));
};

export default ItemsRequestedPanel;
